import { exists, dateFromPartial, sortPartialDate, sortTime, isPartialDate } from 'neuro-utils';
import { compose, equals, filter, map, pickBy, sort, uniq, values } from 'ramda';
import { sortDocumentsDescending } from 'Utility/documentHandling';

/**
 * Background document types originating from the patient's own mobile app. 'msBackground' is deliberately excluded.
 */
export const myBackgroundDocumentTypes = [
  'parkinsonMobileBackground',
  'sleepApneaBackground',
  'mgravisBackground',
] as const satisfies readonly TDocumentType[];

export const backgroundDocumentTypes = ['background', ...myBackgroundDocumentTypes] as const;

/** Background document recorded by the patient in the mobile app */
export type TMyBackgroundDocument = IDocumentWithTypeV2<(typeof myBackgroundDocumentTypes)[number]>;

/**
 * Any document rendered by the background route: clinical document or a patient-recorded one. Discriminated on `_type`.
 */
export type TBackgroundDocument = IDocumentWithTypeV2<'background'> | TMyBackgroundDocument;

/**
 * For the history and form views that read fields dynamically rather than per document type
 */
export type TBackgroundDocumentData = Partial<
  TDocumentDataMap['background'] &
    TDocumentDataMap['parkinsonMobileBackground'] &
    TDocumentDataMap['sleepApneaBackground'] &
    TDocumentDataMap['mgravisBackground']
>;

/** Any background document, viewed through the field-level {@link TBackgroundDocumentData} shape */
export type TAnyBackgroundDocument = TBackgroundDocumentData & IControlProps<(typeof backgroundDocumentTypes)[number]>;

/**
 * Get the latest value and date from any of the documents
 * @param  {{[key:string]:any}[]} docs - All documents
 * @param  {string} name - Name of field
 * @returns {{ date: PartialDate; value: any } | undefined} Date and value of the latest value
 */
export const getLatestValue = (
  docs: IDocumentWithTypeV2<'background'>[],
  name: keyof TDocumentDataMap['background'],
): { date: PartialDate; value: any } | undefined => {
  let returnValue = undefined;
  if (docs.length === 0) return returnValue;

  // Sort by date
  docs.sort(sortDocumentsDescending);
  // Get first occurance of the value from previous docs
  docs
    .filter(d => d._editing === false)
    .some(d => {
      if (exists(d[name])) {
        returnValue = { date: d.date, value: d[name] };
        return true;
      } else return false;
    });
  return returnValue;
};

/**
 * Check that the value exists and it's not null or an object with only a nonexistent value
 * @param  {any} value - Any value to be checked
 * @returns {boolean} True if value exists and isn't null, false if it doesn't exist or is null
 */
export const existsAndIsNotNull = (value: any): boolean => {
  if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
    const objectValue = value.value;
    if (Array.isArray(objectValue)) {
      return objectValue.length > 0 && exists(objectValue[0]) && objectValue[0] !== null;
    }
    return exists(objectValue) && objectValue !== null;
  }
  if (Array.isArray(value)) {
    return value.length > 0 && exists(value[0]) && value[0] !== null;
  }
  return exists(value) && value !== null;
};

/**
 * Merge all documents to one document that contains the latest values recorded and their respective dates
 * @param  {{[key:string]:any}[]} documents - All documents
 * @returns {{ [key: string]: any }} A document that has the latest values as date-value pairs combined from all documents
 */
export const mergeDocuments = (
  documents: TAnyBackgroundDocument[],
): {
  [key in keyof TBackgroundDocumentData]?: { value: TBackgroundDocumentData[key]; date: PartialDate };
} => {
  // Alias the expected return type
  type ReturnType = {
    [key in keyof TBackgroundDocumentData]?: { value: TBackgroundDocumentData[key]; date: PartialDate };
  };

  const merged: ReturnType = {};

  // Safely check for a valid array to prevent "Object is possibly 'undefined'" errors
  if (!Array.isArray(documents) || documents.length === 0) {
    return merged;
  }

  for (const document of documents) {
    // Skip any null/undefined occurrences within the array
    if (!Boolean(document)) continue;

    // Typecast to a generic record to safely access dynamic values without index signature errors
    const doc = document as Record<string, any>;
    const docDate = doc.date;

    for (const key of Object.keys(doc)) {
      // Ignore private keys and the base 'date' parameter
      if (key.startsWith('_') || key === 'date') {
        continue;
      }

      // Assert merged as a generic record explicitly for assigning dynamic string keys
      const mergedRecord = merged as Record<string, any>;

      // If no value for this key yet, add it
      // While mapping `{ date, value }` objects it behaves identically to `!existsAndIsNotNull(merged[key])`.
      if (mergedRecord[key] === undefined) {
        mergedRecord[key] = {
          date: docDate,
          value: doc[key],
        };
      }
    }
  }

  return merged;
};

/**
 * Check if timespan in ongoing at this moment.
 * @param startDate beginning of the timespan as PartialDate
 * @param endDate ending of the timespan as PartialDate
 * @returns true or false
 */
export const isOngoingTimespan = (startDate?: PartialDate | null, endDate?: PartialDate | null): boolean => {
  const dateNow = new Date();
  const startDateAsDate = startDate ? dateFromPartial(startDate) : undefined;
  const endDateAsDate = endDate ? dateFromPartial(endDate) : undefined;
  if (
    (startDateAsDate && startDateAsDate <= dateNow && endDateAsDate === undefined) ||
    (startDateAsDate === undefined && endDateAsDate && dateNow <= endDateAsDate) ||
    (startDateAsDate && startDateAsDate <= dateNow && endDateAsDate && dateNow <= endDateAsDate)
  ) {
    return true;
  } else return false;
};
/**
 * Check if drivingBan array has a ongoing drivingBan
 * @param banArr Array of drivingBans to be checked
 * @returns 1st found drivingban or undefined
 */
export const getOngoingDrivingBan = (banArr: IDrivingBan[]): IDrivingBan | undefined => {
  return banArr.find(ele => isOngoingTimespan(ele.startDate, ele.endDate));
};

type TMeasurementType =
  | 'weight'
  | 'height'
  | 'bmi'
  | 'headCircumference'
  | 'waistCircumference'
  | 'bpSystolic'
  | 'bpDiastolic'
  | 'pulse'
  | 'oxygenSaturation'
  | 'gaf'
  | 'madrs'
  | 'ybopa'
  | 'ybopt'
  | 'ybocs'
  | 'bpSupineSystolic'
  | 'bpSupineDiastolic'
  | 'bpSittingSystolic'
  | 'bpSittingDiastolic'
  | 'bpStandingSystolic'
  | 'bpStandingDiastolic';

// TODO: Import from neuro-schemas
export const codes: { [code: string]: { name: TMeasurementType; unit: string | null } } = {
  '29463-7': { name: 'weight', unit: 'kg' },
  '8302-2': { name: 'height', unit: 'cm' },
  '59574-4': { name: 'bmi', unit: 'kg/m2' },
  '8287-5': { name: 'headCircumference', unit: 'cm' },
  '8280-0': { name: 'waistCircumference', unit: 'cm' },
  '8480-6': { name: 'bpSystolic', unit: 'mmHg' },
  '8462-4': { name: 'bpDiastolic', unit: 'mmHg' },
  '8867-4': { name: 'pulse', unit: 'bpm' },
  '59417-6': { name: 'oxygenSaturation', unit: '%' },
  TMSLP: { name: 'gaf', unit: null },
  '435': { name: 'madrs', unit: null },
  YBOPA: { name: 'ybopa', unit: null },
  YBOPT: { name: 'ybopt', unit: null },
  YBOCS: { name: 'ybocs', unit: null },
  '8461-6': { name: 'bpSupineSystolic', unit: 'mmHg' },
  '8455-8': { name: 'bpSupineDiastolic', unit: 'mmHg' },
  '8459-0': { name: 'bpSittingSystolic', unit: 'mmHg' },
  '8453-3': { name: 'bpSittingDiastolic', unit: 'mmHg' },
  '8460-8': { name: 'bpStandingSystolic', unit: 'mmHg' },
  '8454-1': { name: 'bpStandingDiastolic', unit: 'mmHg' },
};

/**
 * Maps field names to LOINC code object
 * @param {string} name - Field name
 * @returns {{ name: string; unit: string, code: string }|null} LOINC code object
 */
export const fieldNameToCodeObject = (name: string): { name: string; unit: string } | null => {
  const codeObject: { string: { name: string; unit: string } } = pickBy(val => val.name === name, codes);
  return values(codeObject)[0] ?? null;
};

/**
 * Maps field names to LOINC code string
 * @param {string} name - Field name
 * @returns {string|null} LOINC code string
 */
export const fieldNameToCodeString = (name: string): string | null => {
  const codeObject = pickBy(val => val.name === name, codes);
  const key = codeObject && typeof codeObject === 'object' ? Object.keys(codeObject)[0] : undefined;
  return key ?? null;
};

/**
 * Maps care table LOINC codes to a field name
 * @param {string} code - LOINC code
 * @returns {{ name: string; unit: string }|null} field name
 */
export const loincCodeToFieldName = (code: string): { name: string; unit: string | null } | null => codes[code] ?? null;

const getDates = compose<any[], { date: PartialDate }[], PartialDate[], PartialDate[]>(
  uniq,
  map(d => d.date),
  sort((a, b) => sortPartialDate(b.date, a.date)),
);

const commonFields = ['weight', 'height'];
const defaultFields = ['bmi'];
const smadmdFields = ['headCircumference'];
const srFields = ['bmi', 'waistCircumference', 'bpSystolic', 'bpDiastolic', 'pulse', 'oxygenSaturation'];
const ninmtFields = ['gaf', 'madrs', 'ybopa', 'ybopt', 'ybocs'];
const parkinsonFields = [
  'bpSupineSystolic',
  'bpSupineDiastolic',
  'bpSittingSystolic',
  'bpSittingDiastolic',
  'bpStandingSystolic',
  'bpStandingDiastolic',
  'pulse',
];

/**
 * Returns measurement field names for a given platform
 * @param {Platform} platform - Platform name
 * @returns {string[]} Field name list
 */
export const getFields = (platform: Platform | undefined) => {
  let fields = commonFields;
  switch (platform) {
    case 'sma':
    case 'dmd':
      fields = fields.concat(smadmdFields);
      break;
    case 'sleepApnea':
      fields = fields.concat(srFields);
      break;
    case 'ninmt':
      fields = ninmtFields; // Only ninmt fields for now
      break;
    case 'parkinson':
      fields = fields.concat(defaultFields, parkinsonFields);
      break;
    default:
      fields = fields.concat(defaultFields);
      break;
  }
  return fields;
};

export type TCombinedMeasurementData = Array<{
  date: PartialDate;
  data: Array<{
    type: string;
    documents: Array<IDocumentWithTypeV2<'measurement'> | TMyBackgroundDocument>;
  }>;
}>;

/**
 * Group IMeasurements data by date and type
 * [{date, data: [{type, documents: [{code, value, etc}]}]}]
 * @param {IMeasurement[]} docs - Measurement documents
 * @param {Platform|string} platform - Platform
 * @returns {TCombinedMeasurementData} Combined measurements data
 */
export const makeMeasurementsData = (
  docs: Array<IDocumentWithTypeV2<'measurement'>>,
  myDocs: Array<TBackgroundDocument>,
  platform: Platform | undefined,
): TCombinedMeasurementData => {
  const dateArray = getDates(
    [...docs, ...myDocs].filter(d => isPartialDate(d.date)) as Array<
      Required<{ date: PartialDate }> &
        (IMeasurement | ((IParkinsonMobileBackground | ISRMobileBackground | IMgravisBackground) & IControlProps))
    >,
  );
  const combinedDocs = docs as typeof docs | typeof myDocs;
  const dataArray = dateArray.map(date => ({
    date,
    data: getFields(platform).map(type => ({
      type,
      documents: combinedDocs
        .filter(doc => equals(doc.date, date) && 'code' in doc && doc.code === fieldNameToCodeString(type))
        .concat(myDocs.filter(docu => equals(docu.date, date)))
        .sort((a, b) => ('time' in b && 'time' in a ? sortTime(b.time, a.time) : b._cdate - a._cdate)),
    })),
  }));
  return dataArray as TCombinedMeasurementData;
};

/**
 * Get the most recent value and date for the type of measurement
 * @param {TMeasurementType} type - Value type
 * @param {IMeasurement[]} documents - Measurement docs
 * @returns {{value: string, date: PartialDate}|null} Most recent value and date
 */
export const getLatestMeasurementValueAndDate = (
  type: TMeasurementType,
  documents: IDocumentWithTypeV2<'measurement'>[],
): { value?: string; date?: PartialDate } | null => {
  const sortValues = compose<
    any[],
    IDocumentWithTypeV2<'measurement'>[],
    IDocumentWithTypeV2<'measurement'>[],
    { value?: string; date?: PartialDate }[]
  >(
    map(d => ({ value: d.value ?? undefined, date: d.date ?? undefined })),
    filter((t: IMeasurement) => t.code === fieldNameToCodeString(type)),
    sort((a, b) => sortPartialDate(b.date, a.date) || sortTime(b.time, a.time)),
  );
  return sortValues(documents)[0] ?? null;
};

/**
 *
 * @param field field of which the condition is checked
 * @param doc Background document data
 * @param platform selected platform
 * @returns boolean
 */
export const getFieldCondition = (
  field: keyof TFormDocument<IDocumentWithTypeV2<'background'>>,
  doc: TFormDocument<IDocumentWithTypeV2<'background'>>,
  platform: Platform,
): boolean => {
  if (platform === 'epilepsy') {
    switch (field) {
      case 'complicationInfo': {
        if (doc.perinatalComplications !== 'yes') return false;
        return true;
      }
      case 'feverCrampsInfo': {
        if (doc.feverCramps !== 'yes') return false;
        return true;
      }
      case 'severity': {
        if (doc.disabledDiagnosis !== 'yes') return false;
        return true;
      }
      case 'relatives':
      case 'relativityInfo': {
        if (doc.epilepsyFirstDegreeRelative !== 'yes') return false;
        return true;
      }
      case 'driversLicenseGroup': {
        if (doc.driversLicense !== 'yes') return false;
        return true;
      }
      case 'occupation': {
        if (
          !Array.isArray(doc.employment) ||
          (!doc.employment.includes('fullTime') && !doc.employment.includes('partTime'))
        )
          return false;
        return true;
      }
      case 'partTimeHours': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('partTime')) return false;
        return true;
      }
      case 'sickLeaveDays': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('sickLeave')) return false;
        return true;
      }
      case 'unemploymentDays': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('unemployed')) return false;
        return true;
      }
      case 'retirementType': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('retired')) return false;
        return true;
      }
      case 'retirementStart': {
        if (doc.retirementType !== 'disabilityPension') return false;
        return true;
      }
      default: {
        return true;
      }
    }
  }
  if (platform === 'sleepApnea') {
    switch (field) {
      case 'smokingStartYear':
      case 'cigaretteAmountPerDay': {
        if (doc.smoker !== 'currentSmoker' && doc.smoker !== 'formerSmoker') return false;
        return true;
      }
      case 'smokingEndYear': {
        if (doc.smoker !== 'formerSmoker') return false;
        return true;
      }
      case 'annualDrivingKM':
      case 'driversLicenseGroup': {
        if (doc.driversLicense !== 'yes') return false;
        return true;
      }
      case 'drivingBanR1s': {
        if (!Array.isArray(doc.driversLicenseGroup) || !doc.driversLicenseGroup.includes('R1')) return false;
        return true;
      }
      case 'drivingBanR2s':
      case 'professionalDriving': {
        if (!Array.isArray(doc.driversLicenseGroup) || !doc.driversLicenseGroup.includes('R2')) return false;
        return true;
      }
      case 'noProDrivingRequirements': {
        if (doc.professionalDriving !== 'yes') return false;
        return true;
      }
      case 'whichIntoxicants': {
        if (doc.otherIntoxicants !== 'yes') return false;
        return true;
      }
      default: {
        return true;
      }
    }
  }
  if (platform === 'parkinson') {
    switch (field) {
      case 'familyPrevalenceDisease':
      case 'familyMembersParkinson': {
        if (doc.familyPrevalenceParkinson !== 'yes') return false;
        return true;
      }
      case 'siblingDiagnosisAge': {
        if (
          !(
            doc.familyMembersParkinson &&
            doc.familyMembersParkinson.length > 0 &&
            doc.familyMembersParkinson.some(m => m === 'siblings' || m === 'twinSibling') === true
          )
        )
          return false;
        return true;
      }
      case 'occupation': {
        if (
          !Array.isArray(doc.employment) ||
          (!doc.employment.includes('fullTime') && !doc.employment.includes('partTime'))
        )
          return false;
        return true;
      }
      case 'partTimeHours': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('partTime')) return false;
        return true;
      }
      case 'sickLeaveDays': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('sickLeave')) return false;
        return true;
      }
      case 'unemploymentDays': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('unemployed')) return false;
        return true;
      }
      case 'retirementType': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('retired')) return false;
        return true;
      }
      case 'driversLicenseGroup': {
        if (doc.driversLicense !== 'yes') return false;
        return true;
      }
      case 'earlierHeadInjuryYear': {
        if (doc.earlierHeadInjury !== 'yes') return false;
        return true;
      }
      case 'powerOfAttorneyStart': {
        if (doc.powerOfAttorney !== 'yes') return false;
        return true;
      }
      case 'careAllowanceClass': {
        if (doc.careAllowance !== 'yes') return false;
        return true;
      }
      default: {
        return true;
      }
    }
  }
  if (platform === 'ms') {
    switch (field) {
      case 'familyMembersAnamnesisMS': {
        if (doc.familyAnamnesisMS !== 'yes') return false;
        return true;
      }
      case 'employmentOther': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('other')) return false;
        return true;
      }
      case 'pregnancyDueDate': {
        if (doc.pregnancy !== 'pregnant') return false;
        return true;
      }
      default: {
        return true;
      }
    }
  }
  if (platform === 'dmd' || platform === 'sma') {
    if (
      field === 'familyMembers' &&
      doc[`familyMemberDiagnosed${platform.toUpperCase()}` as keyof IBackground] !== 'yes'
    )
      return false;
    return true;
  }
  if (platform === 'huntington') {
    switch (field) {
      case 'familyMembersAnamnesisHuntington': {
        if (doc.familyAnamnesisHuntington !== 'yes') return false;
        return true;
      }
      case 'relativeDegree2FreeText': {
        if (
          !Array.isArray(doc.familyMembersAnamnesisHuntington) ||
          !doc.familyMembersAnamnesisHuntington.includes('relativeDegree2')
        )
          return false;
        return true;
      }
      case 'retirementDate': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('disabilityPension')) return false;
        return true;
      }
      default: {
        return true;
      }
    }
  }
  if (platform === 'mgravis') {
    switch (field) {
      case 'cigaretteAmountPerDay':
      case 'smokingStartYear': {
        if (doc.smoking === 'currentSmoker' || doc.smoking === 'formerSmoker') return true;
        return false;
      }
      case 'smokingEndYear': {
        if (doc.smoking === 'formerSmoker') return true;
        return false;
      }
      case 'snusPortions':
      case 'snusStart': {
        if (doc.snus === 'yes' || doc.snus === 'earlier') return true;
        return false;
      }
      case 'snusEnd': {
        if (doc.snus === 'earlier') return true;
        return false;
      }
      case 'occupation': {
        if (
          !Array.isArray(doc.employment) ||
          (!doc.employment.includes('fullTime') && !doc.employment.includes('partTime'))
        )
          return false;
        return true;
      }
      case 'partTimeHours': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('partTime')) return false;
        return true;
      }
      case 'sickLeaveDays': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('sickLeave')) return false;
        return true;
      }
      case 'unemploymentDays': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('unemployed')) return false;
        return true;
      }
      case 'retirementType': {
        if (!Array.isArray(doc.employment) || !doc.employment.includes('retired')) return false;
        return true;
      }
      case 'workAbilityScore.workAbilityComparedToLifetimeBest' as keyof IBackground: {
        if (['disabilityPension', 'pension', 'unemploymentPension'].includes(doc.retirementType ?? '')) return false;
        return true;
      }
      case 'workAbilityScore.reductionReason' as keyof IBackground:
      case 'workAbilityScore.reductionType' as keyof IBackground: {
        if (
          doc.workAbilityScore?.workAbilityComparedToLifetimeBest &&
          doc.workAbilityScore.workAbilityComparedToLifetimeBest <= 7
        )
          return true;
        return false;
      }
      case 'workAbilityIndex.selfAssessmentAbilityToWorkIn2Years' as keyof IBackground: {
        if (
          Array.isArray(doc.employment) &&
          (doc.employment.includes('fullTime') || doc.employment.includes('partTime'))
        ) {
          return true;
        }
        return false;
      }
      default: {
        return true;
      }
    }
  }
  if (platform === 'polyneuro') {
    switch (field) {
      case 'rarePolyneuropathyInFirstDegreeRelatives': {
        if (doc.rarePolyneuropathyInTheFamily === 'yes') return true;
        return false;
      }

      default: {
        return true;
      }
    }
  }
  return true;
};
